import { oc } from "@orpc/contract";
import { z } from "zod";
import { InvoiceStatusSchema, agingBasisSchema } from "./ar-invoices.contract";

/**
 * AR Aging Report RPC Contract
 *
 * Provides an endpoint for fetching customer-grouped aging buckets:
 * current (not yet due), 1-30 days overdue, 31-60, 61-90, and 90+ days.
 * Computed server-side using Postgres GROUP BY for scalability (101K+ invoices).
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * A user-defined aging bucket range.
 * minDays=0 means "not yet due" (current).
 * Omit maxDays for an open-ended bucket (e.g., 91+ days).
 */
export const AgingBucketDefinitionSchema = z
  .object({
    label: z.string(),
    minDays: z.number().int().nonnegative(),
    maxDays: z.number().int().nonnegative().optional(),
  })
  .refine((b) => b.maxDays === undefined || b.maxDays >= b.minDays, {
    message: "maxDays must be >= minDays",
  });

export type AgingBucketDefinition = z.infer<typeof AgingBucketDefinitionSchema>;

/** Standard 5-bucket aging layout used when no custom buckets are specified. */
export const DEFAULT_AGING_BUCKETS: AgingBucketDefinition[] = [
  { label: "Current", minDays: 0 },
  { label: "1-30 Days", minDays: 1, maxDays: 30 },
  { label: "31-60 Days", minDays: 31, maxDays: 60 },
  { label: "61-90 Days", minDays: 61, maxDays: 90 },
  { label: "90+ Days", minDays: 91 },
];

/**
 * Input for the aging report
 */
export const GetArAgingInputSchema = z.object({
  customerId: UUIDSchema.optional(),
  /** Filter to specific customers by ID */
  customerIds: z.array(UUIDSchema).optional(),
  /**
   * Calculation basis:
   *   "days_overdue"         = days past due date
   *   "days_since_invoice"   = days since invoice date
   *   "days_since_shipment"  = days since ship date
   */
  basis: agingBasisSchema.default("days_since_shipment"),
  /** Filter by invoice status. Defaults to excluding closed/voided if not provided. */
  status: z.array(InvoiceStatusSchema).optional(),
  /** Only include invoices at least this many days old (based on the selected basis).
   *  0 is treated as "no minimum" — equivalent to omitting the field.
   *  To filter to not-yet-due invoices (the old "current" bucket meaning of
   *  minDays=0), set `currentOnly: true` instead. */
  minDays: z.number().int().nonnegative().optional(),
  /** Only include invoices at most this many days old (based on the selected basis). */
  maxDays: z.number().int().nonnegative().optional(),
  /** When true, restrict results to invoices whose reference date is in the
   *  future ("not yet due" / "current bucket"). Only meaningful when
   *  basis === "days_overdue"; for ship/invoice-date bases the reference
   *  date is rarely in the future. When set, minDays/maxDays are ignored. */
  currentOnly: z.boolean().optional(),
  /** Custom bucket definitions. When omitted, uses the standard 5 buckets.
   *  Must be sorted, non-overlapping, and contiguous (each minDays = prev maxDays + 1).
   *  The last bucket may omit maxDays for an open-ended range. */
  buckets: z
    .array(AgingBucketDefinitionSchema)
    .refine(
      (buckets) => {
        if (buckets.length === 0) return true;
        for (let i = 1; i < buckets.length; i++) {
          const prev = buckets[i - 1];
          const curr = buckets[i];
          if (prev.maxDays !== undefined) {
            // Normal bounded bucket: next must start at maxDays + 1
            if (curr.minDays !== prev.maxDays + 1) return false;
          } else if (prev.minDays === 0) {
            // "Current" bucket (minDays=0, no maxDays): next must start at 1
            if (curr.minDays !== 1) return false;
          } else {
            // Non-current open-ended bucket in the middle is invalid
            return false;
          }
        }
        return true;
      },
      {
        message:
          "Buckets must be sorted and contiguous: each bucket's minDays must equal the previous bucket's maxDays + 1",
      },
    )
    .optional(),
});

/**
 * A single customer's aging breakdown
 */
export const ArAgingCustomerRowSchema = z.object({
  customerId: UUIDSchema,
  customerName: z.string().nullable(),
  /**
   * The customer's name as the source ERP renders it (Famous shows
   * `Acme Foods (ACM)`). Display-only — `customerName` stays the raw value so
   * sorting, filtering, and export are unaffected.
   */
  erpDisplayName: z.string().nullable(),
  /** Balance not yet due (dueDate >= today) */
  current: DecimalStringSchema,
  /** 1-30 days overdue */
  days1_30: DecimalStringSchema,
  /** 31-60 days overdue */
  days31_60: DecimalStringSchema,
  /** 61-90 days overdue */
  days61_90: DecimalStringSchema,
  /** 90+ days overdue */
  days90plus: DecimalStringSchema,
  /** Per-bucket amounts, parallel to response bucketDefinitions */
  bucketAmounts: z.array(DecimalStringSchema),
  /** Sum of all unpaid buckets */
  totalOutstanding: DecimalStringSchema,
  /** Total payments applied */
  totalApplied: DecimalStringSchema,
  /** Count of open invoices for this customer */
  invoiceCount: z.int().nonnegative(),
});

/**
 * Grand totals row summed across all customers
 */
export const ArAgingTotalsSchema = z.object({
  current: DecimalStringSchema,
  days1_30: DecimalStringSchema,
  days31_60: DecimalStringSchema,
  days61_90: DecimalStringSchema,
  days90plus: DecimalStringSchema,
  bucketAmounts: z.array(DecimalStringSchema),
  totalOutstanding: DecimalStringSchema,
  totalApplied: DecimalStringSchema,
});

/**
 * Get AR aging response schema
 */
export const GetArAgingResponseSchema = z.object({
  customers: z.array(ArAgingCustomerRowSchema),
  totals: ArAgingTotalsSchema,
  /** Bucket definitions describing what each index in bucketAmounts represents */
  bucketDefinitions: z.array(AgingBucketDefinitionSchema),
});

/**
 * AR Aging Contracts
 */
export const arAgingContracts = {
  get: oc
    .input(GetArAgingInputSchema)
    .output(GetArAgingResponseSchema)
    .route({ method: "GET" }),
} as const;

// Type exports
export type GetArAgingInput = z.input<typeof GetArAgingInputSchema>;
export type ArAgingCustomerRow = z.infer<typeof ArAgingCustomerRowSchema>;
export type ArAgingTotals = z.infer<typeof ArAgingTotalsSchema>;
export type GetArAgingResponse = z.infer<typeof GetArAgingResponseSchema>;
